import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceOperatorService, { DatevExportRow, saveBlob } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';

const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');
const iso = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Vormonat als [erster, letzter Tag] im Format YYYY-MM-DD. */
export const previousMonthRange = (now = new Date()): [string, string] => [
    iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    iso(new Date(now.getFullYear(), now.getMonth(), 0)),
];

/** DATEV-Export der Plattform-Sicht (Endpunkte aus T4): Zeitraum wählen, CSV laden, Exporthistorie. */
const OperatorDatevPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [[from, to], setRange] = useState<[string, string]>(previousMonthRange());
    const [exports, setExports] = useState<DatevExportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            setExports(await marketplaceOperatorService.getDatevExports());
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError')));
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const create = async () => {
        if (!from || !to || to < from) {
            notify(translate('marketplaceOperator.datev.rangeInvalid'), { type: 'warning' });
            return;
        }
        setCreating(true);
        try {
            const result = await marketplaceOperatorService.createDatevExport(from, to);
            saveBlob(result.blob, result.filename);
            notify(translate('marketplaceOperator.datev.created', { count: result.bookingCount }), { type: 'success' });
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const download = async (row: DatevExportRow) => {
        try {
            saveBlob(await marketplaceOperatorService.downloadDatevExport(row.id), row.filename);
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        }
    };

    const scope = (row: DatevExportRow) => translate(`marketplaceOperator.datev.scope.${row.scope}`, { _: row.scope });

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('marketplaceOperator.datev.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 2, maxWidth: 760 }}>{translate('marketplaceOperator.datev.intro')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <TextField size="small" type="date" label={translate('marketplaceOperator.datev.from')} value={from} onChange={(e) => setRange([e.target.value, to])} slotProps={{ inputLabel: { shrink: true } }} />
                <TextField size="small" type="date" label={translate('marketplaceOperator.datev.to')} value={to} onChange={(e) => setRange([from, e.target.value])} slotProps={{ inputLabel: { shrink: true } }} />
                <Button variant="contained" onClick={create} disabled={creating}>{translate('marketplaceOperator.datev.create')}</Button>
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{translate('marketplaceOperator.datev.history')}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rectangular" height={120} />
            ) : exports.length === 0 && !error ? (
                <Alert severity="info">{translate('marketplaceOperator.datev.empty')}</Alert>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['created', 'period', 'scope', 'bookings', 'file', 'actions'].map((c) => <TableCell key={c}>{translate(`marketplaceOperator.datev.columns.${c}`)}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exports.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{fmt(row.createdAt)}</TableCell>
                                        <TableCell>{fmt(row.periodStart)} – {fmt(row.periodEnd)}</TableCell>
                                        <TableCell>{scope(row)}</TableCell>
                                        <TableCell>{row.bookingCount}</TableCell>
                                        <TableCell sx={{ wordBreak: 'break-all' }}>{row.filename}</TableCell>
                                        <TableCell><Button size="small" variant="outlined" onClick={() => download(row)}>{translate('marketplaceOperator.datev.download')}</Button></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
                        {exports.map((row) => (
                            <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                                <Typography sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{row.filename}</Typography>
                                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>
                                    {fmt(row.periodStart)} – {fmt(row.periodEnd)} · {scope(row)} · {row.bookingCount}
                                </Typography>
                                <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => download(row)}>{translate('marketplaceOperator.datev.download')}</Button>
                            </Paper>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default OperatorDatevPanel;
